import { Store } from '@ngxs/store';
import { Observable, map, switchMap } from 'rxjs';
import { UserApiParams } from 'src/app/services/user/user.service';
import { ManagerState } from 'src/app/store/manager/manager.state';
import { LazySelectionModel } from 'src/app/util';
import { User, UserGroup, UserRole } from 'src/models';

export interface LazyUserSelectionSerializable {
  all?: boolean;
  list?: string[];
  type?: 'users' | 'groups';
}

export class LazyUserSelection {
  static readonly membersOfNoGroupId = '@membersOfNoGroup';

  static none = new LazyUserSelection('users', LazySelectionModel.none, LazySelectionModel.none);
  static all = new LazyUserSelection('users', LazySelectionModel.all, LazySelectionModel.all);
  static default = new LazyUserSelection('groups', LazySelectionModel.all, LazySelectionModel.all);
  static selfUser = LazyUserSelection.singleUser('');

  static singleUser(userId: string) {
    if (userId == null) { return null; }
    return LazyUserSelection.fromSerializable({ type: 'users', list: [userId] });
  }

  static fromSerializable(val: LazyUserSelectionSerializable) {
    if (!val) {
      return this.default;
    }

    const sel = new LazySelectionModel(val.list || [], !!val.all);
    const type = val.type || this.default.type;
    const users = type === 'users' ? sel : LazySelectionModel.all;
    const groups = type === 'groups' ? sel : LazySelectionModel.all;

    return new LazyUserSelection(type, users, groups);
  }

  static getFilteredManagedUsers(store: Store, userFilter$: Observable<LazyUserSelection>): Observable<User[]> {
    return userFilter$.pipe(
      switchMap(userFilter => store.select(ManagerState.users.onReports.includeArchived(userFilter.includeArchivedUsers)).pipe(
        map(users => (users || []).filter(userFilter.isUserSelected)),
      ),
      ));
  }

  static same(oldSelection: LazyUserSelection, newSelection: LazyUserSelection) {
    if (!(oldSelection && newSelection)) { return false; }
    if (oldSelection.type !== newSelection.type) { return false; }
    if (oldSelection.includeArchivedUsers !== newSelection.includeArchivedUsers) { return false; }
    if (oldSelection.type === 'users') { return LazySelectionModel.same(oldSelection.users, newSelection.users); }
    if (oldSelection.type === 'groups') { return LazySelectionModel.same(oldSelection.groups, newSelection.groups); }
    return false;
  }

  constructor(
    public readonly type: 'users' | 'groups',
    public readonly users: LazySelectionModel,
    public readonly groups: LazySelectionModel,
    public readonly allGroups?: UserGroup[],
    public readonly noTag = false,
    public readonly multipleTag = false,
    public readonly includeArchivedUsers = false,
  ) { }


  setType(type: 'users' | 'groups') {
    return new LazyUserSelection(type, this.users, this.groups, this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
  }

  setAllUsers(users: string[] | Set<string>, allUsersCount?: number): LazyUserSelection {
    return new LazyUserSelection(this.type, this.users.setAllItems(users, allUsersCount), this.groups,
      this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
  }

  setAllGroups(groups: UserGroup[], allGroupsCount?: number): LazyUserSelection {
    return new LazyUserSelection(this.type, this.users,
      this.groups.setAllItems(null, allGroupsCount), groups, this.noTag, this.multipleTag, this.includeArchivedUsers);
  }

  select(...ids: string[]): LazyUserSelection {
    if (this.type === 'users') {
      return new LazyUserSelection(this.type, this.users.select(...ids), this.groups,
        this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
    } else {
      return new LazyUserSelection(this.type, this.users, this.groups.select(...ids),
        this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
    }
  }

  deselect(...ids: string[]): LazyUserSelection {
    if (this.type === 'users') {
      return new LazyUserSelection(this.type, this.users.deselect(...ids), this.groups,
        this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
    } else {
      return new LazyUserSelection(this.type, this.users, this.groups.deselect(...ids),
        this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
    }
  }

  set(selected: boolean, ...ids: string[]): LazyUserSelection {
    if (this.type === 'users') {
      return new LazyUserSelection(this.type, this.users.set(selected, ...ids), this.groups, this.allGroups,
        this.noTag, this.multipleTag, this.includeArchivedUsers);
    } else {
      return new LazyUserSelection(this.type, this.users, this.groups.set(selected, ...ids), this.allGroups,
        this.noTag, this.multipleTag, this.includeArchivedUsers);
    }
  }

  isSelected(id: string): boolean {
    if (this.type === 'users') {
      return this.users.isSelected(id);
    } else {
      return this.groups.isSelected(id);
    }
  }

  clear(): LazyUserSelection {
    if (this.type === 'users') {
      return new LazyUserSelection(this.type, this.users.clear(), this.groups,
        this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
    } else {
      return new LazyUserSelection(this.type, this.users, this.groups.clear(), this.allGroups, false, false, false);
    }
  }

  selectAll(): LazyUserSelection {
    if (this.type === 'users') {
      return new LazyUserSelection(this.type, this.users.selectAll(), this.groups,
        this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
    } else {
      return new LazyUserSelection(this.type, this.users, this.groups.selectAll(),
        this.allGroups, false, false, this.includeArchivedUsers);
    }
  }

  switchGroupsAll(): LazyUserSelection {
    const groups = new LazySelectionModel(this.allGroups?.map(x => x.id), false, null, this.groups.countAll);
    return new LazyUserSelection(this.type, this.users, groups, this.allGroups, this.noTag, this.multipleTag, this.includeArchivedUsers);
  }

  setNoTag(noTag: boolean) {
    return new LazyUserSelection(this.type, this.users, noTag ? this.groups.clear() : this.groups,
      this.allGroups, noTag, noTag ? false : this.multipleTag, this.includeArchivedUsers);
  }

  setIncludeArchivedUsers(includeArchivedUsers: boolean, archivedUsersIds: string[] = []) {
    return new LazyUserSelection(this.type, this.all ? this.users : this.users.deselect(...archivedUsersIds), this.groups,
      this.allGroups, this.noTag, this.multipleTag, includeArchivedUsers);
  }

  setMultipleTag(multipleTag: boolean) {
    return new LazyUserSelection(this.type, this.users, multipleTag ? this.groups.clear() : this.groups,
      this.allGroups, multipleTag ? false : this.noTag, multipleTag, this.includeArchivedUsers);
  }

  get count(): number {
    return this.type === 'users' ? this.users.count : this.groups.count;
  }

  get userCount(): number {
    return (this.type === 'users' || this.groups.all)
      ? this.users.count
      : undefined;
    // TODO: We can't know the total number of users in selected groups as there may be intersections. Thus, the code below is wrong
    // : this.allGroups?.filter(x => this.groups.isSelected(x.id)).reduce((acc, cur) => acc + cur.usersOnReports, 0);
  }

  get any(): boolean {
    return this.type === 'users' ? this.users.any : this.groups.any;
  }

  get all(): boolean {
    return this.type === 'users' ? this.users.all : this.groups.all;
  }

  get indeterminate(): boolean {
    return this.type === 'users' ? this.users.indeterminate : this.groups.indeterminate;
  }

  get includeArchivedUsersAsApiParam(): { 'include-archived-users': 1 } {
    return this.includeArchivedUsers ? { 'include-archived-users': 1 } : null;
  }

  isUserSelected = (user: { id: string, tagIds?: string[], role?: UserRole }) => {
    if (!user) {
      return false;
    }
    if (this.type === 'users') {
      if (this.users.all) {
        return true;
      }
      return this.users.isSelected(user.id);
    } else {
      if (this.groups.all) {
        return true;
      }
      if (!user.tagIds) {
        return false;
      }
      return this.groups.list.some(x => {
        const membersOfNoGroupIdCondition = user.role === 'user' ? user.tagIds.length < 2 : user.tagIds.length < 1;
        return x === LazyUserSelection.membersOfNoGroupId ? membersOfNoGroupIdCondition : user.tagIds.includes(x);
      });
    }
  };

  toSerializable() {
    const all = this.all;
    const type = this.type;
    const notAllCondition = type === 'users' ? this.users.list : this.groups.list;
    const list = all ? [] : notAllCondition;

    return { all, list, type } as LazyUserSelectionSerializable;
  }

  asApiParams(allUsersKey: string = 'all-on-reports'): { user: string[] | string, tag?: string[], 'include-archived-users'?: 1 } {
    if (!this.any) { return { user: [] }; }
    if (this.all) { return { user: allUsersKey, ...this.includeArchivedUsersAsApiParam }; }

    return this.type === 'users'
      ? { user: this.users.list || [], ...this.includeArchivedUsersAsApiParam }
      : { tag: this.groups.list, user: allUsersKey, ...this.includeArchivedUsersAsApiParam };
  }

  asUserApiParams(allUsersKey: string = 'all-on-reports') {
    if (this.noTag) {
      return { 'no-tag': true };
    }
    if (this.multipleTag) {
      return { 'filter[tag-count]': '2_' };
    }

    const apiParams = this.asApiParams(allUsersKey);

    return {
      ...apiParams.tag && { tag: apiParams.tag },
      ...apiParams.user && apiParams.user !== allUsersKey && { 'filter[id]': apiParams.user },
      ...this.includeArchivedUsersAsApiParam,
    } as UserApiParams;
  }

  getUserCounts(onReports: boolean = true): { max?: number, min?: number, exact?: number } {
    if (this.type === 'users' || this.groups.all) {
      const count = this.users.count;
      return { max: count, min: count, exact: count };
    }

    const selectedGroups = this.allGroups?.filter(x => this.groups.isSelected(x.id));

    if (!selectedGroups) { return {}; }

    const max = selectedGroups.reduce((acc, cur) => acc + (onReports ? cur.usersOnReports : cur.users), 0);
    const min = selectedGroups.reduce((acc, cur) => Math.max(acc, (onReports ? cur.usersOnReports : cur.users)), 0);

    return { max, min };
  }
}
